import type { ItemHealth } from '@/common/api/types/dashboard/health';
import type { ItemStatus } from '@/common/api/types/itemStatus';

export type DependencyImpactState = 'none' | 'error' | 'warning' | 'neutral';
export type DependencySeverity = 'neutral' | 'warning' | 'error';

export const SEVERITY_TEXT_COLOR: Record<DependencySeverity, string> = {
  neutral: 'text-text-neutral-secondary',
  warning: 'text-text-warning',
  error: 'text-text-error',
};

export interface PredecessorImpactRow {
  id: number;
  itemStatusId?: number | null;
  plannedFinishDateTime?: string | null;
  forecastedFinishDateTime?: string | null;
}

export interface SuccessorImpactRow {
  id: number;
  itemStatusId?: number | null;
  plannedStartDateTime?: string | null;
  forecastedStartDateTime?: string | null;
}

const isDoneStatus = (
  itemStatusId: number | null | undefined,
  itemStatuses: ItemStatus[],
): boolean => itemStatuses.find((status) => status.id === itemStatusId)?.schedulingType === 'DONE';

export const hasNoPredecessorDates = (row: PredecessorImpactRow): boolean =>
  !row.plannedFinishDateTime && !row.forecastedFinishDateTime;

export const hasNoSuccessorDates = (row: SuccessorImpactRow): boolean =>
  !row.plannedStartDateTime && !row.forecastedStartDateTime;

export interface PredecessorImpactOptions {
  row: PredecessorImpactRow;
  currentItemHealth: ItemHealth | undefined;
  itemStatuses: ItemStatus[];
  anchorStartDate: string | null | undefined;
}

export const getPredecessorImpactState = ({
  row,
  currentItemHealth,
  itemStatuses,
  anchorStartDate,
}: PredecessorImpactOptions): DependencyImpactState => {
  if (isDoneStatus(row.itemStatusId, itemStatuses)) {
    return 'none';
  }

  const healthBlocked =
    currentItemHealth?.dependency?.blockedByPredecessorIds.includes(row.id) ?? false;
  const healthAtRisk =
    !healthBlocked &&
    (currentItemHealth?.dependency?.atRiskPredecessorIds.includes(row.id) ?? false);

  // A predecessor only counts as blocking when its own finish is later than our start —
  // otherwise it finishes in time and isn't the actual holdup, even if our start has passed.
  const predecessorFinish = row.forecastedFinishDateTime ?? row.plannedFinishDateTime;
  const exceedsAnchor =
    !!anchorStartDate && !!predecessorFinish && predecessorFinish > anchorStartDate;

  const isBlocked = healthBlocked || (!healthAtRisk && exceedsAnchor);
  if (isBlocked) {
    return 'error';
  }
  if (healthAtRisk) {
    return 'warning';
  }

  return hasNoPredecessorDates(row) ? 'neutral' : 'none';
};

export interface SuccessorImpactOptions {
  row: SuccessorImpactRow;
  currentItemId: number;
  currentItemStatusId: number | null | undefined;
  healthByItemId: Map<number, ItemHealth | undefined>;
  itemStatuses: ItemStatus[];
  anchorFinishDate: string | null | undefined;
}

export const getSuccessorImpactState = ({
  row,
  currentItemId,
  currentItemStatusId,
  healthByItemId,
  itemStatuses,
  anchorFinishDate,
}: SuccessorImpactOptions): DependencyImpactState => {
  // A finished item can't be making anyone late, regardless of what its stored
  // finish date says — check both sides of the "am I blocking it" relationship.
  if (
    isDoneStatus(row.itemStatusId, itemStatuses) ||
    isDoneStatus(currentItemStatusId, itemStatuses)
  ) {
    return 'none';
  }

  const rowHealth = healthByItemId.get(row.id);
  const healthBlocking =
    rowHealth?.dependency?.blockedByPredecessorIds.includes(currentItemId) ?? false;
  const healthAtRisk =
    !healthBlocking &&
    (rowHealth?.dependency?.atRiskPredecessorIds.includes(currentItemId) ?? false);

  // We only count as making this successor late when our own finish is later than its
  // start — a different predecessor delaying it is out of scope for this relationship.
  const successorStart = row.forecastedStartDateTime ?? row.plannedStartDateTime;
  const exceedsAnchor = !!anchorFinishDate && !!successorStart && anchorFinishDate > successorStart;

  const isBlocking = healthBlocking || (!healthAtRisk && exceedsAnchor);
  if (isBlocking) {
    return 'error';
  }
  if (healthAtRisk) {
    return 'warning';
  }

  return hasNoSuccessorDates(row) ? 'neutral' : 'none';
};
